import React, { useState } from "react";
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, LogIn, AlertCircle, Loader2 } from "lucide-react";
import { authAPI } from "../../api/authApi";
import styles from "./Login.module.css";
import logo from './logo.jpg';

function LoginPage() {
    const navigate = useNavigate();
    const [data, setData] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setData(prev => ({ ...prev, [name]: value }));
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const response = await authAPI.login_client(data);
            localStorage.setItem('token', response.token);
            navigate('/');
        } catch (err) {
            console.error(err);
            setError('E-mail ou senha incorretos.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            {/* Efeitos de iluminação de fundo (Blobs) */}
            <div className={styles.blobBlue} />
            <div className={styles.blobGold} />

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className={styles.card}
            >
                {/* Destaque para a Logo */}
                <div className={styles.logoWrapper}>
                    <img src={logo} alt="Logo" className={styles.logoImage} />
                </div>

                <header className={styles.header}>
                    <h1 className={styles.title}>Entrar</h1>
                    <p className={styles.subtitle}>Acesse sua conta para continuar.</p>
                </header>

                <form onSubmit={handleSubmit} className={styles.form} noValidate>
                    {/* Campo de E-mail */}
                    <div className={styles.field}>
                        <label className={styles.label}>E-mail</label>
                        <div className={styles.inputWrapper}>
                            <Mail className={styles.icon} size={18} />
                            <input
                                type="email"
                                name="email"
                                placeholder="seu@email.com"
                                value={data.email}
                                onChange={handleChange}
                                className={`${styles.input} ${error ? styles.inputError : ''}`}
                                required
                            />
                        </div>
                    </div>

                    {/* Campo de Senha */}
                    <div className={styles.field}>
                        <label className={styles.label}>Senha</label>
                        <div className={styles.inputWrapper}>
                            <Lock className={styles.icon} size={18} />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                placeholder="••••••••"
                                value={data.password}
                                onChange={handleChange}
                                className={`${styles.input} ${error ? styles.inputError : ''}`}
                                required
                            />
                            <button
                                type="button"
                                className={styles.toggleBtn}
                                onClick={() => setShowPassword(s => !s)}
                                tabIndex="-1"
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    {/* Alerta de Erro */}
                    <AnimatePresence mode="wait">
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className={styles.errorContainer}
                            >
                                <AlertCircle size={16} /> <span>{error}</span>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Botão de Submit */}
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        className={styles.primary}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <Loader2 className={styles.spinner} size={20} />
                        ) : (
                            <>
                                <LogIn size={18} />
                                <span>Entrar</span>
                            </>
                        )}
                    </motion.button>

                    {/* Links de Rodapé */}
                    <div className={styles.rowBetween}>
                        <Link to="/register" className={styles.link}>Criar conta</Link>
                        <Link to="/forgot-password" className={styles.link}>Esqueci minha senha</Link>
                    </div>
                </form>
            </motion.div>
        </div>
    );
}

export default LoginPage;